import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Fade,
  Grow,
  LinearProgress,
  Alert,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Business as BusinessIcon,
  AttachMoney as FundingIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Computer as TechIcon,
  Category as CategoryIcon,
  TrendingUp as GrowthIcon,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';

const CardBasedResearchCriteria = () => {
  const [cards, setCards] = useState([]);
  const [showQuestionDialog, setShowQuestionDialog] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverBucket, setDragOverBucket] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const questions = [
    {
      id: 'industry',
      question: 'What industry or sector are you researching?',
      type: 'text',
      placeholder: 'e.g., B2B SaaS, Healthcare Tech, E-commerce',
      icon: <BusinessIcon />,
      generateCards: (answer) => answer ? [{
        id: `card-${Date.now()}`,
        title: answer,
        type: 'industry',
        icon: <BusinessIcon />,
        bucket: 'mustHave',
        suggestedBucket: 'mustHave',
        impact: 'high',
        confidence: 95
      }] : []
    },
    {
      id: 'companySize',
      question: 'What size companies are you interested in?',
      type: 'select',
      options: [
        'Startups (1-50 employees)',
        'Mid-Market (51-500 employees)',
        'Enterprise (500+ employees)',
        'Any size'
      ],
      icon: <PeopleIcon />,
      generateCards: (answer) => {
        if (answer === 'Any size') return [];
        return [{
          id: `card-${Date.now()}`,
          title: answer,
          type: 'size',
          icon: <PeopleIcon />,
          bucket: 'niceToHave',
          suggestedBucket: 'mustHave',
          impact: 'medium',
          confidence: 75
        }];
      }
    },
    {
      id: 'funding',
      question: 'Any specific funding requirements?',
      type: 'multiselect',
      options: [
        'Pre-seed/Seed',
        'Series A',
        'Series B+',
        'Profitable/Bootstrapped',
        'No requirement'
      ],
      icon: <FundingIcon />,
      generateCards: (answers) => {
        if (!answers || answers.includes('No requirement')) return [];
        return answers.filter(a => a).map(answer => ({
          id: `card-${Date.now()}-${Math.random()}`,
          title: answer,
          type: 'funding',
          icon: <FundingIcon />,
          bucket: 'niceToHave',
          suggestedBucket: 'niceToHave',
          impact: 'medium',
          confidence: 80
        }));
      }
    },
    {
      id: 'disqualify',
      question: 'What should automatically disqualify a company?',
      type: 'text',
      placeholder: 'e.g., Agencies, Consultancies, Non-profits',
      icon: <BlockIcon />,
      generateCards: (answer) => answer ? [{
        id: `card-${Date.now()}`,
        title: `No ${answer}`,
        type: 'disqualify',
        icon: <BlockIcon />,
        bucket: 'disqualify',
        suggestedBucket: 'disqualify',
        impact: 'critical',
        confidence: 100
      }] : []
    },
    {
      id: 'technology',
      question: 'Any specific technologies or platforms?',
      type: 'text',
      placeholder: 'e.g., AWS, Shopify, Salesforce integration',
      icon: <TechIcon />,
      optional: true,
      generateCards: (answer) => answer ? [{
        id: `card-${Date.now()}`,
        title: answer,
        type: 'technology',
        icon: <TechIcon />,
        bucket: 'niceToHave',
        suggestedBucket: 'niceToHave',
        impact: 'low',
        confidence: 60
      }] : []
    }
  ];

  const buckets = [
    {
      id: 'mustHave',
      title: 'Must Have',
      subtitle: 'Required criteria',
      color: '#d32f2f',
      bgColor: '#ffebee',
      icon: <CheckIcon />
    },
    {
      id: 'niceToHave', 
      title: 'Nice to Have',
      subtitle: 'Bonus points',
      color: '#1976d2',
      bgColor: '#e3f2fd',
      icon: <StarIcon />
    },
    {
      id: 'disqualify',
      title: 'Disqualify',
      subtitle: 'Auto-exclude',
      color: '#f57c00',
      bgColor: '#fff3e0',
      icon: <BlockIcon />
    }
  ];

  const handleQuestionAnswer = () => {
    const question = questions[currentQuestion];
    const answer = questionAnswers[question.id];
    
    if (answer && answer !== 'No requirement') {
      const newCards = question.generateCards(answer);
      setCards(prev => [...prev, ...newCards]);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowQuestionDialog(false);
      setTimeout(() => setShowSuggestions(true), 500);
    }
  };

  const handleSkipQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowQuestionDialog(false);
      setTimeout(() => setShowSuggestions(true), 500);
    }
  };

  const handleDragStart = (e, card) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, bucketId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBucket(bucketId);
  };

  const handleDragLeave = () => {
    setDragOverBucket(null);
  };

  const handleDrop = (e, targetBucket) => {
    e.preventDefault();
    setDragOverBucket(null);
    
    if (draggedCard && targetBucket !== draggedCard.bucket) {
      setCards(prev => prev.map(card => 
        card.id === draggedCard.id 
          ? { ...card, bucket: targetBucket }
          : card
      ));
    }
    setDraggedCard(null);
  };

  const handleDeleteCard = (cardId) => {
    setCards(prev => prev.filter(card => card.id !== cardId));
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
  };

  const handleSaveEdit = () => {
    setCards(prev => prev.map(card => 
      card.id === editingCard.id ? editingCard : card
    ));
    setEditingCard(null);
  };

  const applySuggestions = () => {
    setCards(prev => prev.map(card => ({
      ...card,
      bucket: card.suggestedBucket
    })));
    setShowSuggestions(false);
  };

  const getImpactColor = (impact) => {
    switch(impact) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getCardsByBucket = (bucketId) => {
    return cards.filter(card => card.bucket === bucketId);
  };

  const calculateQualificationEstimate = () => {
    const mustHaveCount = getCardsByBucket('mustHave').length;
    const disqualifyCount = getCardsByBucket('disqualify').length;
    
    if (mustHaveCount === 0) return 90;
    if (mustHaveCount <= 2) return 75;
    if (mustHaveCount <= 4) return 50;
    return 25;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog 
        open={showQuestionDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <PsychologyIcon />
            <Box flex={1}>
              <Typography variant="h6">Let's Build Your Criteria</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Question {currentQuestion + 1} of {questions.length}
              </Typography>
            </Box>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(currentQuestion / questions.length) * 100}
            sx={{ 
              mt: 2, 
              height: 6, 
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white',
                borderRadius: 3
              }
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'white', pt: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            {questions[currentQuestion].icon}
            <Typography variant="h6">
              {questions[currentQuestion].question}
            </Typography>
          </Box>
          
          {questions[currentQuestion].type === 'text' && (
            <TextField
              fullWidth
              placeholder={questions[currentQuestion].placeholder}
              value={questionAnswers[questions[currentQuestion].id] || ''}
              onChange={(e) => setQuestionAnswers(prev => ({
                ...prev,
                [questions[currentQuestion].id]: e.target.value
              }))}
              variant="outlined"
              autoFocus
            />
          )}

          {questions[currentQuestion].type === 'select' && (
            <FormControl fullWidth>
              <Select
                value={questionAnswers[questions[currentQuestion].id] || ''}
                onChange={(e) => setQuestionAnswers(prev => ({
                  ...prev,
                  [questions[currentQuestion].id]: e.target.value
                }))}
              >
                {questions[currentQuestion].options.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {questions[currentQuestion].type === 'multiselect' && (
            <FormControl fullWidth>
              <Select
                multiple
                value={questionAnswers[questions[currentQuestion].id] || []}
                onChange={(e) => setQuestionAnswers(prev => ({
                  ...prev,
                  [questions[currentQuestion].id]: e.target.value
                }))}
                renderValue={(selected) => selected.join(', ')}
              >
                {questions[currentQuestion].options.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {questions[currentQuestion].optional && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Optional - you can skip this
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'white', p: 2 }}>
          <Button onClick={handleSkipQuestion} sx={{ mr: 'auto' }}>
            Skip
          </Button>
          <Button 
            variant="contained" 
            onClick={handleQuestionAnswer}
            disabled={!questions[currentQuestion].optional && !questionAnswers[questions[currentQuestion].id]}
          >
            {currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}
          </Button>
        </DialogActions>
      </Dialog>

      {showSuggestions && cards.some(card => card.bucket !== card.suggestedBucket) && (
        <Fade in timeout={500}>
          <Alert 
            severity="info" 
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <>
                <Button color="inherit" size="small" onClick={applySuggestions}>
                  Apply
                </Button>
                <IconButton size="small" onClick={() => setShowSuggestions(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
            icon={<AutoAwesomeIcon />}
          >
            <strong>AI Suggestions:</strong> We've analyzed your criteria and suggest moving some cards for better results
          </Alert>
        </Fade>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Organize Your Research Criteria
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drag cards between buckets to refine your search. Estimated qualification: ~{calculateQualificationEstimate()}%
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setCurrentQuestion(0);
            setQuestionAnswers({});
            setShowQuestionDialog(true);
          }}
        >
          Add More Cards
        </Button>
      </Box>

      <Box display="flex" gap={3} sx={{ minHeight: 400 }}>
        {buckets.map(bucket => (
          <Paper
            key={bucket.id}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 2,
              backgroundColor: bucket.bgColor,
              border: dragOverBucket === bucket.id ? `2px dashed ${bucket.color}` : 'none',
              transition: 'all 0.3s ease',
              transform: dragOverBucket === bucket.id ? 'scale(1.02)' : 'scale(1)'
            }}
            onDragOver={(e) => handleDragOver(e, bucket.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, bucket.id)}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box sx={{ color: bucket.color }}>{bucket.icon}</Box>
              <Box flex={1}>
                <Typography variant="h6" sx={{ color: bucket.color, fontWeight: 'bold' }}>
                  {bucket.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bucket.subtitle}
                </Typography>
              </Box>
              <Badge badgeContent={getCardsByBucket(bucket.id).length} color="primary" />
            </Box>

            <Box sx={{ minHeight: 300 }}>
              {getCardsByBucket(bucket.id).map((card, index) => (
                <Grow in key={card.id} timeout={300 + index * 100}>
                  <Card
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      cursor: 'move',
                      backgroundColor: 'white',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, card)}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIcon sx={{ color: '#bdbdbd', fontSize: 20 }} />
                      <Box sx={{ color: bucket.color }}>{card.icon}</Box>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {card.title}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <Chip 
                            label={card.impact} 
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: getImpactColor(card.impact),
                              color: 'white'
                            }}
                          />
                          {card.bucket !== card.suggestedBucket && (
                            <Tooltip title={`Suggested: ${card.suggestedBucket}`}>
                              <LightbulbIcon sx={{ fontSize: 16, color: '#ffc107' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <IconButton size="small" onClick={() => handleEditCard(card)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteCard(card.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grow>
              ))}
              
              {getCardsByBucket(bucket.id).length === 0 && (
                <Box
                  sx={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: bucket.color,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    opacity: 0.5
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Drop cards here
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      <Dialog open={!!editingCard} onClose={() => setEditingCard(null)}>
        <DialogTitle>Edit Criteria Card</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Criteria"
            value={editingCard?.title || ''}
            onChange={(e) => setEditingCard(prev => ({ ...prev, title: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingCard(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CardBasedResearchCriteria;